import React, { useEffect, useState } from 'react';
import {
  Dialog, Button, IconButton, ComboBox, TextInput, Checkbox,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import {
  TALMUDA_TRACTATES, TRACTATE_NAMES, EMPTY_RANGE, EMPTY_TRACTATE_ENTRY, selectionToEntries, entriesToSelection, isGemaraEntriesValid,
} from './gemaraSelectionModel.js';
import { Row } from '../next/ui';

/**
 * New-design versions of GemaraSelectionEditor / GemaraExamineEditDialog — same controlled value (entries[])
 * and the same model helpers, rendered with crm-kit/next.
 */
const TRACTATE_OPTIONS = TRACTATE_NAMES.map((n) => ({ value: n, label: n }));

export function GemaraSelectionEditorNext({ value = [], onChange, showExamined = true, title }) {
  const entries = value?.length ? value : [EMPTY_TRACTATE_ENTRY()];
  const set = (fn) => onChange?.(fn(entries));
  const patch = (tIdx, p) => set((prev) => prev.map((e, i) => (i === tIdx ? { ...e, ...p } : e)));
  const patchRange = (tIdx, rIdx, field, v) => set((prev) => prev.map((e, i) => (i !== tIdx ? e : { ...e, ranges: e.ranges.map((r, j) => (j === rIdx ? { ...r, [field]: v } : r)) })));
  const addRange = (tIdx) => set((prev) => prev.map((e, i) => (i === tIdx ? { ...e, ranges: [...e.ranges, EMPTY_RANGE()] } : e)));
  const removeRange = (tIdx, rIdx) => set((prev) => prev.map((e, i) => (i !== tIdx || e.ranges.length <= 1 ? e : { ...e, ranges: e.ranges.filter((_, j) => j !== rIdx) })));

  return (
    <div className="nx-stack" style={{ gap: 10 }}>
      {title ? <h3 className="nx-sub-h">{title}</h3> : null}
      {entries.map((entry, tIdx) => {
        const maxDaf = TALMUDA_TRACTATES[entry.tractate] || 999;
        return (
          <div key={tIdx} className="nx-item" style={{ alignItems: 'flex-start' }}>
            <div className="nx-stack" style={{ flex: 1, gap: 8 }}>
              <Row>
                <div style={{ width: 220 }}>
                  <ComboBox
                    value={entry.tractate ? { value: entry.tractate, label: entry.tractate } : null}
                    onChange={(o) => patch(tIdx, { tractate: o?.value || '' })}
                    options={TRACTATE_OPTIONS}
                    placeholder="מסכתא…"
                    ariaLabel="מסכתא"
                  />
                </div>
                {entry.tractate && maxDaf < 999 ? <small className="ck-muted">{`עד דף ${maxDaf}`}</small> : null}
                <span className="ck-grow" />
                <IconButton icon="trash" label="הסרת מסכתא" disabled={entries.length <= 1} onClick={() => set((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== tIdx)))} />
              </Row>
              {entry.ranges.map((range, rIdx) => (
                <Row key={rIdx}>
                  <TextInput type="number" ltr min={2} max={maxDaf} style={{ width: 96 }} placeholder="מדף" value={range.from} onChange={(e) => patchRange(tIdx, rIdx, 'from', e.target.value)} />
                  <span className="ck-muted">—</span>
                  <TextInput type="number" ltr min={2} max={maxDaf} style={{ width: 96 }} placeholder="עד דף" value={range.to} onChange={(e) => patchRange(tIdx, rIdx, 'to', e.target.value)} />
                  <IconButton size="sm" icon="x" label="הסרת טווח" disabled={entry.ranges.length <= 1} onClick={() => removeRange(tIdx, rIdx)} />
                </Row>
              ))}
              <Row>
                <Button size="sm" variant="ghost" icon="plus" disabled={!entry.tractate} onClick={() => addRange(tIdx)}>טווח דפים נוסף</Button>
                {showExamined ? <Checkbox checked={!!entry.examined} onChange={() => patch(tIdx, { examined: !entry.examined })} label="נבחן במסכת זו" /> : null}
              </Row>
            </div>
          </div>
        );
      })}
      <div><Button size="sm" icon="plus" onClick={() => set((prev) => [...prev, EMPTY_TRACTATE_ENTRY()])}>הוסף מסכתא</Button></div>
    </div>
  );
}

export function GemaraExamineEditDialogNext({
  open, memberName, initialSelection = [], onSave, saving = false, onClose,
  subtitle = 'ערוך את טווחי הדפים וסמן את המסכתות שהנרשם נבחן בהן בפועל:',
}) {
  const [entries, setEntries] = useState(() => selectionToEntries(initialSelection));
  useEffect(() => {
    if (open) setEntries(selectionToEntries(initialSelection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const valid = isGemaraEntriesValid(entries);
  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      title={memberName ? `סמן נבחן — ${memberName}` : 'סמן נבחן'}
      icon="check"
      size="md"
      footer={(
        <>
          <Button variant="ghost" disabled={saving} onClick={onClose}>ביטול</Button>
          <Button variant="primary" icon="check" disabled={saving || !valid} onClick={() => { if (valid && !saving) onSave?.(entriesToSelection(entries)); }}>{saving ? 'שומר…' : 'שמירה'}</Button>
        </>
      )}
    >
      <div className="nx-stack">
        {subtitle ? <p className="nx-desc">{subtitle}</p> : null}
        <GemaraSelectionEditorNext value={entries} onChange={setEntries} showExamined />
      </div>
    </Dialog>
  );
}
